import React, { useEffect, useState } from "react";
import { FaArrowRight, FaBars, FaChevronDown, FaTimes } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV_ITEMS } from "../../../data/navigation";
import CbtEntryLink from "../../portal/CbtEntryLink";
import PortalEntryLink from "../../portal/PortalEntryLink";
import Head from "./Head";
import "./header.css";

const isApplicantPath = (path) => path === "/admissions/apply";
const isPortalAction = (item = {}) => item.kind === "portal";
const isCbtAction = (item = {}) => item.kind === "cbt";
const isApplicantAction = (item = {}) => item.kind === "applicant" || isApplicantPath(item.path);

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState("");

  const handleNavLinkClick = (path) => {
    // Close menus immediately so body scroll lock is released before navigation
    setOpenDropdown("");
    setMenuOpen(false);
    navigate(path);
  };

  const closeMenus = () => {
    setOpenDropdown("");
    setMenuOpen(false);
  };

  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown("");
  }, [location.pathname]);

  useEffect(() => {
    // Mobile menu: avoid locking scroll for the entire page.
    // The menu itself already scrolls (see header.css: .nav-links { overflow-y: auto }).
    if (!menuOpen) return;

    // Only lock body scroll on larger screens where the menu does not scroll.
    // On mobile, this prevented users from seeing/scrolling the home content.
    const shouldLockBodyScroll = window.innerWidth > 1100;
    if (!shouldLockBodyScroll) return;

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isItemActive = (path) => {
    if (!path) {
      return false;
    }

    if (path === "/") {
      return location.pathname === "/";
    }

    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const renderNavItemLink = (item, className, onClick) => {
    if (isPortalAction(item)) {
      return (
        <PortalEntryLink
          className={className}
          role={item.role || "educator"}
          onClick={onClick}
        >
          {item.label}
        </PortalEntryLink>
      );
    }

    if (isCbtAction(item)) {
      return (
        <CbtEntryLink className={className} onClick={onClick}>
          {item.label}
        </CbtEntryLink>
      );
    }

    if (isApplicantAction(item)) {
      return (
        <PortalEntryLink
          className={className}
          role="applicant"
          onClick={onClick}
        >
          {item.label}
        </PortalEntryLink>
      );
    }

    return (
      <Link
        className={className}
        to={item.path}
        onClick={(event) => {
          onClick?.(event);

          if (event.defaultPrevented) {
            return;
          }

          event.preventDefault();
          handleNavLinkClick(item.path);
        }}
      >
        <span>{item.label}</span>
      </Link>
    );
  };

  const toggleDropdown = (label) => {
    setOpenDropdown((current) => (current === label ? "" : label));
  };

  return (
    <>
      <div className="site-header">
        <Head />
      </div>

      <header className={`main-header ${menuOpen ? "menu-open" : ""}`}>
        <nav className="nav-shell">
          <ul
            id="primary-navigation"
            className={`nav-links ${menuOpen ? "mobile-nav-open" : ""}`}
          >
            <li className="mobile-portal-links" aria-label="Portal quick links">
              <PortalEntryLink
                className="mobile-portal-link"
                role="educator"
                onClick={closeMenus}
              >
                Educator
              </PortalEntryLink>
              <PortalEntryLink
                className="mobile-portal-link"
                role="student"
                onClick={closeMenus}
              >
                Student
              </PortalEntryLink>
              <PortalEntryLink
                className="mobile-portal-link"
                role="parent"
                onClick={closeMenus}
              >
                Parent
              </PortalEntryLink>
              <CbtEntryLink
                className="mobile-portal-link"
                onClick={closeMenus}
              >
                CBT
              </CbtEntryLink>
            </li>

            {NAV_ITEMS.map((item) => {
              const hasChildren = Boolean(item.children?.length);
              const dropdownOpen = openDropdown === item.label;
              const active = item.path ? isItemActive(item.path) : dropdownOpen;
              const submenuId = `submenu-${item.label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")}`;

              return (
                <li
                  key={item.label}
                  className={`nav-item ${hasChildren ? "has-children" : ""} ${dropdownOpen ? "dropdown-open" : ""
                    } ${active ? "active" : ""}`}
                >
                  <div className="nav-link-row">
                    {item.path ? (
                      renderNavItemLink(item, "nav-link", closeMenus)
                    ) : (
                      <button
                        type="button"
                        className="nav-link nav-link-button"
                        aria-controls={hasChildren ? submenuId : undefined}
                        aria-haspopup={hasChildren ? "true" : undefined}
                        aria-expanded={dropdownOpen}
                        onClick={() => toggleDropdown(item.label)}
                      >
                        <span>{item.label}</span>
                      </button>
                    )}

                    {hasChildren ? (
                      <button
                        type="button"
                        className="submenu-toggle"
                        aria-label={`Toggle ${item.label} submenu`}
                        aria-controls={submenuId}
                        aria-haspopup="true"
                        aria-expanded={dropdownOpen}
                        onClick={() => toggleDropdown(item.label)}
                      >
                        <FaChevronDown />
                      </button>
                    ) : null}
                  </div>

                  {hasChildren ? (
                    <ul
                      id={submenuId}
                      className={`submenu ${dropdownOpen ? "submenu-open" : ""}`}
                    >
                      {item.children.map((child) => (
                        <li key={child.label}>
                          {renderNavItemLink(
                            child,
                            child.path && isItemActive(child.path) ? "active" : "",
                            closeMenus
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>

          <div className="nav-actions">
            <PortalEntryLink
              className="header-cta"
              role="applicant"
              onClick={closeMenus}
            >
              Apply Now
              <FaArrowRight />
            </PortalEntryLink>

            <button
              type="button"
              className="nav-toggle"
              aria-label="Toggle navigation"
              aria-controls="primary-navigation"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((current) => !current)}
            >
              {menuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </nav>
      </header>
    </>
  );
}

export default Header;
